import { ITEM_PREVIEW_LIST_FORMS } from "$lib/utilities/constants";
import { correctedUrl } from "$lib/utilities/url-helpers";

import HorizontalImageCard from "$lib/components/Card/Horizontal/ImageCard.svelte";
import HorizontalContainedImageCard from "$lib/components/Card/Horizontal/ContainedImageCard.svelte";
import AvatarCard from "$lib/components/Card/Avatar/ImageCard.svelte";
import ImageCard from "$lib/components/Card/Vertical/ImageCard.svelte";
import ContainedImageCard from "$lib/components/Card/Vertical/ContainedImageCard.svelte";
import TextCard from "$lib/components/Card/Vertical/TextCard.svelte";
import Resource from "$lib/components/Card/Vertical/Resource.svelte";
import HeadlineCard from "$lib/components/Card/Vertical/HeadlineCard.svelte";
const {
  HEADLINE,
  AVATAR_CARD_MEDIUM,
  AVATAR_CARD_SMALL,
  AVATAR_CARD_XSMALL,
  TEXT_CARD_LARGE,
  TEXT_CARD_MEDIUM,
  TEXT_CARD_SMALL,
  TEXT_CARD_LARGE_CONTAINED,
  TEXT_CARD_MEDIUM_CONTAINED,
  TEXT_CARD_SMALL_CONTAINED,
  HORIZONTAL_FULL_WIDTH,
  HORIZONTAL_2UP,
  HORIZONTAL_FULL_WIDTH_CONTAINED,
  HORIZONTAL_2UP_CONTAINED,
  VERTICAL_CARD_2UP,
  VERTICAL_CARD_3UP,
  VERTICAL_CARD_4UP,
  VERTICAL_CARD_2UP_CONTAINED,
  VERTICAL_CARD_3UP_CONTAINED,
  VERTICAL_CARD_4UP_CONTAINED,
  TEXT_CARD_2UP,
  TEXT_CARD_3UP,
  TEXT_CARD_4UP,
  RESOURCE
} = ITEM_PREVIEW_LIST_FORMS;

const itemComponents = {
  [AVATAR_CARD_MEDIUM]: AvatarCard,
  [AVATAR_CARD_SMALL]: AvatarCard,
  [AVATAR_CARD_XSMALL]: AvatarCard,
  [TEXT_CARD_LARGE]: TextCard,
  [TEXT_CARD_MEDIUM]: TextCard,
  [TEXT_CARD_SMALL]: TextCard,
  [TEXT_CARD_LARGE_CONTAINED]: TextCard,
  [TEXT_CARD_MEDIUM_CONTAINED]: TextCard,
  [TEXT_CARD_SMALL_CONTAINED]: TextCard,
  [HORIZONTAL_FULL_WIDTH]: HorizontalImageCard,
  [HORIZONTAL_2UP]: HorizontalImageCard,
  [HORIZONTAL_FULL_WIDTH_CONTAINED]: HorizontalContainedImageCard,
  [HORIZONTAL_2UP_CONTAINED]: HorizontalContainedImageCard,
  [VERTICAL_CARD_2UP]: ImageCard,
  [VERTICAL_CARD_3UP]: ImageCard,
  [VERTICAL_CARD_4UP]: ImageCard,
  [VERTICAL_CARD_2UP_CONTAINED]: ContainedImageCard,
  [VERTICAL_CARD_3UP_CONTAINED]: ContainedImageCard,
  [VERTICAL_CARD_4UP_CONTAINED]: ContainedImageCard,
  [TEXT_CARD_2UP]: TextCard,
  [TEXT_CARD_3UP]: TextCard,
  [TEXT_CARD_4UP]: TextCard,
  [RESOURCE]: Resource,
  [HEADLINE]: HeadlineCard
};

/**
 * Generate the props for the <Grid /> component
 * @param {string} itemsFormFactor - the itemsFormFactor for the IPL
 * @returns the props for the <Grid /> component
 */
export function createListProps(itemsFormFactor) {
  switch (itemsFormFactor) {
    // 1up
    case HEADLINE:
    case RESOURCE:
    case HORIZONTAL_FULL_WIDTH:
    case HORIZONTAL_FULL_WIDTH_CONTAINED:
      return {
        columns: {
          lg: 1,
          md: 1,
          sm: 1
        }
      };

    // 2up
    case VERTICAL_CARD_2UP:
    case VERTICAL_CARD_2UP_CONTAINED:
    case TEXT_CARD_2UP:
      return {
        columns: {
          lg: 2,
          md: 2,
          sm: 1
        }
      };
    case HORIZONTAL_2UP:
    case HORIZONTAL_2UP_CONTAINED:
      return {
        columns: {
          lg: 2,
          md: 1,
          sm: 1
        }
      };

    // 3up
    case VERTICAL_CARD_3UP:
      return {
        columns: {
          lg: 3,
          md: 2,
          sm: 1
        }
      };
    case VERTICAL_CARD_3UP_CONTAINED:
    case TEXT_CARD_3UP:
      return {
        columns: {
          lg: 3,
          md: 3,
          sm: 1
        }
      };
    case AVATAR_CARD_MEDIUM:
      return {
        columns: {
          lg: 3,
          md: 2,
          sm: 1
        }
      };

    // 4up
    case VERTICAL_CARD_4UP:
    case VERTICAL_CARD_4UP_CONTAINED:
    case AVATAR_CARD_SMALL:
    case TEXT_CARD_4UP:
      return {
        columns: {
          lg: 4,
          md: 3,
          sm: 1
        }
      };

    // 6up
    case AVATAR_CARD_XSMALL:
      return {
        columns: {
          lg: 6,
          md: 4,
          sm: 1
        }
      };

    default:
      return {
        columns: {
          lg: 3,
          md: 2,
          sm: 1
        }
      };
  }
}

/**
 * Generate the props for each card component
 * @param {Object} item - data representing each card in the IPL
 * @param {string} itemsFormFactor - the itemsFormFactor for the IPL
 * @returns the props for the card component
 */
export function createItemProps(item, itemsFormFactor) {
  let headlineFields;
  let cardFields;

  switch (
    item.contentType // correctedUrl used to relativize url of each item in list
  ) {
    case "navTarget":
      headlineFields = {
        title: item.text,
        url: correctedUrl(item.url),
        description: item.name
      };
      cardFields = {
        title: item.text,
        url: correctedUrl(item.url),
        description: item.description,
        imageComponent: {
          fields: {
            altText: item.description,
            imageAsset: item.image
          }
        }
      };
      break;
    case "pageData": // fallthrough
    default:
      headlineFields = {
        title: item.headline,
        url: correctedUrl(item.relativeUrl),
        description: item.previewText
      };
      cardFields = {
        title: item.headline,
        url: correctedUrl(item.relativeUrl),
        imageComponent: item.previewImageComponent,
        description: item.previewText
      };
      break;
  }

  switch (itemsFormFactor) {
    case HEADLINE:
      return {
        timestamp: item.legacyPublishDate || item.sys?.createdAt,
        ...headlineFields
      };

    case HORIZONTAL_FULL_WIDTH:
    case HORIZONTAL_FULL_WIDTH_CONTAINED:
      return {
        fullWidth: true,
        resizeParams: { fit: "scale" },
        widthsAt: { lg: "200px", md: "33vw", sm: "33vw" },
        ...cardFields
      };

    case HORIZONTAL_2UP:
    case HORIZONTAL_2UP_CONTAINED:
      return {
        size: "2up",
        resizeParams: { fit: "scale" },
        widthsAt: { lg: "150px", md: "33vw", sm: "33vw" },
        ...cardFields
      };

    case VERTICAL_CARD_2UP:
    case VERTICAL_CARD_2UP_CONTAINED:
      return {
        size: "2up",
        resizeParams: { fit: "scale" },
        widthsAt: { lg: "600px", md: "50vw", sm: "100vw" },
        ...cardFields
      };
    case VERTICAL_CARD_3UP:
    case VERTICAL_CARD_3UP_CONTAINED:
      return {
        size: "3up",
        resizeParams: { fit: "scale" },
        widthsAt: { lg: "360px", md: "50vw", sm: "100vw" },
        id: item?.sys?.id ? `vertical-card-3up-${item.sys.id}` : "",
        ...cardFields
      };
    case VERTICAL_CARD_4UP:
    case VERTICAL_CARD_4UP_CONTAINED:
      return {
        size: "4up",
        resizeParams: { fit: "scale" },
        widthsAt: { lg: "275px", md: "33vw", sm: "100vw" },
        ...cardFields
      };

    case AVATAR_CARD_MEDIUM:
      return {
        size: "3up",
        name: cardFields.title,
        role: cardFields.description,
        resizeParams: { fit: "scale" },
        widthsAt: { lg: "360px", md: "50vw", sm: "25vw" },
        ...cardFields
      };
    case AVATAR_CARD_SMALL:
      return {
        size: "4up",
        name: cardFields.title,
        role: cardFields.description,
        resizeParams: { fit: "scale" },
        widthsAt: { lg: "275px", md: "33vw", sm: "25vw" },
        ...cardFields
      };
    case AVATAR_CARD_XSMALL:
      return {
        size: "6up",
        name: cardFields.title,
        role: cardFields.description,
        resizeParams: { fit: "scale" },
        widthsAt: { lg: "175px", md: "25vw", sm: "25vw" },
        ...cardFields
      };

    case TEXT_CARD_2UP:
      return {
        size: "2up",
        ...cardFields
      };
    case TEXT_CARD_3UP:
      return {
        size: "3up",
        ...cardFields
      };
    case TEXT_CARD_4UP:
      return {
        size: "4up",
        ...cardFields
      };

    case RESOURCE:
      return {
        title: item.name || item.headline,
        description: item.source || item.previewText,
        url: item.url || item.relativeUrl
      };

    default:
      return {};
  }
}

/**
 * Determine the itemsFormFactor used to render the card
 * @param {object} data - the data representing the IPL component from Contentful
 * @param {Array<{conditionsMet:boolean, newItemsFormFactor:string}>} overrides - if conditionsMet == True, use the newItemsFormFactor instead of the original itemsFormFactor from Contentful
 * @returns the itemsFormFactor used to determine card component to render
 */
export function getItemsFormFactor(data, overrides) {
  for (const { conditionsMet, newItemsFormFactor } of overrides) {
    if (conditionsMet) {
      return newItemsFormFactor;
    }
  }
  return data.fields.itemsFormFactor;
}

/**
 * Determine which component to use to render each card
 * @param {string} itemsFormFactor - the itemsFormFactor for the IPL
 * @returns the card component to render
 */
export function getItemComponent(itemsFormFactor) {
  return itemComponents[itemsFormFactor];
}

/**
 * Determine items per page for IPL with pagination
 * @param {string} itemsFormFactor - the itemsFormFactor for the IPL
 * @param {number} defaultItemsPerPage - the default items per page = 36
 * @returns the itmes per page for IPL with pagination
 */
export function getItemsPerPage(itemsFormFactor, defaultItemsPerPage = 36) {
  if (itemsFormFactor == HEADLINE) {
    return 10;
  }
  return defaultItemsPerPage;
}

export function createItemContainerClass(itemsFormFactor) {
  switch (itemsFormFactor) {
    case RESOURCE:
      return "w-full lg:w-auto";
    default:
      return "";
  }
}
